import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatMoney } from "@/lib/format";
import { PAYMENT_METHODS } from "@/lib/bookingConstants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Wallet } from "lucide-react";

export default function EncaisserDialog({ booking, open, onOpenChange, onPaid }) {
  const { orgId } = useAuth();
  const qc = useQueryClient();
  const [method, setMethod] = useState("cash");

  const mut = useMutation({
    mutationFn: async () => (await api.post(`/api/bookings/${booking.id}/pay`, { payment_method: method }, { params: { organization_id: orgId } })).data,
    onSuccess: () => {
      toast.success("Paiement encaissé");
      qc.invalidateQueries();
      onPaid?.();
      onOpenChange(false);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  if (!booking) return null;
  const amount = formatMoney(booking.total_amount_cents, booking.currency);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="encaisser-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wallet className="h-5 w-5 text-primary" /> Encaisser la réservation</DialogTitle>
          <DialogDescription className="sr-only">Confirmez le mode de paiement pour encaisser le montant total de la réservation.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-slate-50 p-3 text-sm">
            <Row k="Réservation" v={booking.booking_reference} />
            <Row k="Client" v={booking.customer_name} />
            <Row k="Montant" v={<span className="font-mono text-base font-bold text-slate-900">{amount}</span>} last />
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">Mode de paiement</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(PAYMENT_METHODS).map(([k, label]) => (
                <button key={k} type="button" onClick={() => setMethod(k)} data-testid={`payment-method-${k}`}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${method === k ? "border-primary bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <button disabled={mut.isPending} onClick={() => mut.mutate()} data-testid="confirm-payment-button"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Confirmer l'encaissement — {amount}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ k, v, last }) => (
  <div className={`flex items-center justify-between py-1.5 ${last ? "" : "border-b border-slate-200"}`}>
    <span className="text-slate-500">{k}</span><span className="font-medium text-slate-900">{v}</span>
  </div>
);
